import { SaveSerializer, CURRENT_SAVE_VERSION } from '@/systems/SaveSerializer';
import type { SaveBackend, GameSnapshot, SnapshotInput } from '@/systems/SaveSerializer';
import type { GameState } from './GameState';
import type { EventBus } from './EventBus';

export type SaveSlot = 'auto' | 'manual1' | 'manual2' | 'manual3' | 'hardcore';

export interface SaveManagerOptions {
  backend: SaveBackend;
  bus: EventBus;
  state: GameState;
  isHardcore?: () => boolean;
  backupsPerSlot?: number;
  autosaveIntervalMs?: number;
  slotPrefix?: string;
}

interface PersistedEnvelope {
  slot: SaveSlot;
  snapshot: GameSnapshot;
  savedAt: number;
}

/**
 * Wraps the pure SaveSerializer with:
 * - Multiple named slots (auto + 3 manual + hardcore)
 * - Rolling backups per slot (last N, keyed with -bak1/-bak2/...)
 * - Atomic write pattern (temp key -> rename by swapping pointers)
 * - Bus-driven autosave (tier changes, ability unlocks, nest entry)
 * - Periodic autosave on an interval
 * - Hardcore mode: single slot, wipes on death
 */
export class SaveManager {
  private readonly serializers = new Map<SaveSlot, SaveSerializer>();
  private readonly disposers: Array<() => void> = [];
  private autosaveTimer: ReturnType<typeof setInterval> | null = null;
  private dirty = false;

  constructor(private readonly options: SaveManagerOptions) {}

  /** Start listening to the bus and schedule periodic autosaves. */
  start(): void {
    const { bus } = this.options;

    this.disposers.push(bus.on('mass:tier-change', () => this.markDirty()));
    this.disposers.push(bus.on('ability:unlocked', () => this.markDirty()));
    this.disposers.push(bus.on('sentience:change', () => this.markDirty()));
    this.disposers.push(bus.on('tentacles:change', () => this.markDirty()));
    this.disposers.push(
      bus.on('blob:death', () => {
        if (this.options.isHardcore?.()) {
          void this.wipeHardcore();
        } else {
          this.markDirty();
        }
      }),
    );
    this.disposers.push(
      bus.on('arc:enter', () => {
        this.markDirty();
      }),
    );
    this.disposers.push(
      bus.on('arc:complete', () => {
        void this.saveSlot('auto');
      }),
    );

    const interval = this.options.autosaveIntervalMs ?? 30_000;
    this.autosaveTimer = setInterval(() => {
      if (this.dirty) void this.saveSlot('auto');
    }, interval);
  }

  stop(): void {
    if (this.autosaveTimer !== null) {
      clearInterval(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    for (const d of this.disposers) d();
    this.disposers.length = 0;
  }

  /** Flush any pending dirty save to the auto slot. */
  async flush(): Promise<void> {
    if (this.dirty) await this.saveSlot('auto');
  }

  /** Write a snapshot atomically to the named slot, rotating backups. */
  async saveSlot(slot: SaveSlot): Promise<void> {
    const snapshot = this.buildSnapshot(slot);
    const envelope: PersistedEnvelope = {
      slot,
      snapshot,
      savedAt: Date.now(),
    };

    // Rotate backups before overwriting the live slot
    const backupsPerSlot = this.options.backupsPerSlot ?? 3;
    await this.rotateBackups(slot, backupsPerSlot);

    // Atomic-ish write: stage a temp serializer, copy to live on success
    const temp = this.serializerFor(`${slot}-tmp`);
    await temp.snapshot(envelope.snapshot);
    const staged = await temp.restore();
    if (!staged) throw new Error(`Save failed: temp write did not persist for slot ${slot}`);
    await this.serializerFor(slot).snapshot(envelope.snapshot);
    await temp.clear();

    this.dirty = false;
  }

  async loadSlot(slot: SaveSlot): Promise<GameSnapshot | null> {
    const snap = await this.serializerFor(slot).restore();
    if (snap) return snap;

    // Fall back through backups if primary is missing/corrupt
    const backups = this.options.backupsPerSlot ?? 3;
    for (let i = 1; i <= backups; i++) {
      const bak = await this.serializerFor(`${slot}-bak${i}`).restore();
      if (bak) return bak;
    }
    return null;
  }

  async listSlots(): Promise<Array<{ slot: SaveSlot; snapshot: GameSnapshot | null }>> {
    const slots: SaveSlot[] = ['auto', 'manual1', 'manual2', 'manual3', 'hardcore'];
    const results = await Promise.all(
      slots.map(async (slot) => ({
        slot,
        snapshot: await this.serializerFor(slot).restore(),
      })),
    );
    return results;
  }

  async deleteSlot(slot: SaveSlot): Promise<void> {
    await this.serializerFor(slot).clear();
    const backups = this.options.backupsPerSlot ?? 3;
    for (let i = 1; i <= backups; i++) {
      await this.serializerFor(`${slot}-bak${i}`).clear();
    }
  }

  /** Apply a snapshot to live GameState (e.g. on Continue from menu). */
  applySnapshotToState(snapshot: GameSnapshot): void {
    const { state } = this.options;
    state.mass.setMass(snapshot.mass);
    state.brain.setState({
      value: snapshot.brain,
      humanConsumed: snapshot.humanBrainConsumed,
    });
    state.abilities.setState({
      counts: snapshot.speciesCounts,
      unlocked: snapshot.abilitiesUnlocked,
      techPoints: snapshot.techPoints,
    });
  }

  private markDirty(): void {
    this.dirty = true;
  }

  private async wipeHardcore(): Promise<void> {
    await this.deleteSlot('hardcore');
  }

  private buildSnapshot(slot: SaveSlot): GameSnapshot {
    const input = this.currentInput(slot === 'hardcore');
    return {
      version: CURRENT_SAVE_VERSION,
      timestamp: Date.now(),
      ...input,
    };
  }

  private currentInput(hardcore: boolean): SnapshotInput {
    const { state } = this.options;
    return {
      mass: state.mass.currentMass,
      brain: state.brain.currentValue,
      humanBrainConsumed: state.brain.humanBrainConsumed,
      abilitiesUnlocked: [...state.abilities.allUnlocked()],
      speciesCounts: { ...state.abilities.allCounts() },
      techPoints: state.abilities.techPointsAvailable,
      isHardcore: hardcore,
    };
  }

  private async rotateBackups(slot: SaveSlot, count: number): Promise<void> {
    // Slide bakN-1 -> bakN, bakN-2 -> bakN-1, ..., primary -> bak1
    for (let i = count; i >= 1; i--) {
      const src = i === 1 ? slot : `${slot}-bak${i - 1}`;
      const dst = `${slot}-bak${i}`;
      const raw = await this.options.backend.load(src);
      if (raw) await this.options.backend.save(dst, raw);
    }
  }

  private serializerFor(slot: string): SaveSerializer {
    const cached = this.serializers.get(slot as SaveSlot);
    if (cached) return cached;
    const ser = new SaveSerializer(this.options.backend, slot);
    this.serializers.set(slot as SaveSlot, ser);
    return ser;
  }
}
